import {subWeeks} from "date-fns";
import {useEffect, useRef, useState} from "react";
import ReportUtils from "../../../utils/report-utils.js";
import EncounterXrayService from "../../../services/reports/encounter-xray-service.js";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import SubScroll from "../../../components/sub-scroll/sub-scroll.jsx";
import {Template} from "../../../components/template/template.jsx";
import HeaderButtons from "../../../components/reports/report-menu/header-buttons.jsx";
import Menu from "../../../components/reports/report-menu/menu.jsx";
import Filters from "./components/filters.jsx";
import AutoDatetime from "../../../components/reports/auto-datetime/auto-datetime.jsx";
import {Card} from "../../../components/card/card.jsx";
import NotAppliedContent from "../../../components/reports/report-menu/not-applied-content.jsx";
import NoDataContent from "../../../components/reports/report-menu/no-data-content.jsx";
import Content from "./components/content.jsx";
import {useQuery} from "@tanstack/react-query";
import bosService from "../../../services/web/bos-service.js";

const initialFilters = () => ({
    dtStart: subWeeks(new Date(), 1),
    dtEnd: new Date(),
    user: [],
});

const filterLabels ={
    dtStart: "Data/hora inicial",
    dtEnd: "Data/hora final",
    user: "Abordado por",
}

const createConfig = (filterLabels) => ({
    url: "/encounter_x_ray/apply",
    filterLabels,

    buildFilters: (filters, utils) => ({
        dtStart: filters.dtStart ? utils.formatDateTime(filters.dtStart) : null,
        dtEnd: filters.dtEnd ? utils.formatDateTime(filters.dtEnd) : null,
        userId: filters.user?.map(i => i.value) ?? [],
    }),

    buildAuditFilters: (filters, formatted, utils) =>
        utils.filters({
            ...filters,
            dtStart: formatted.dtStart,
            dtEnd: formatted.dtEnd
        }, filterLabels, true),

    validatePeriod: (filters) => ReportUtils.periodValidate(filters.dtStart, filters.dtEnd)
});

export default function EncounterXray() {
    const [isLoading, setIsLoading] = useState(false);
    const [collapsed, setCollapsed] = useState(false);
    const [fixedTop, setFixedTop] = useState(null);
    const [reportData, setReportData] = useState(null);
    const hasUrlFilters = () => {return ["dtStart", "dtEnd"].some(param => ReportUtils.getUrlParam(param))};
    const [filters, setFilters] = useState(() => ReportUtils.buildUrlFilters({initialFilters, includeTeam: false}));
    const [headerSelected, setHeaderSelected] = useState(-1);
    const printComponentRef = useRef(null);
    const buttonRef = useRef(null);
    const title = "Raio X de abordagens";

    const { data: boName } = useQuery({
        queryKey: ['bo-name'],
        queryFn: () => bosService.getBoName(),
        initialData: 'BO'
    });

    useEffect(() => {
        if (!reportData) return;

        ReportUtils.appendFilters(
            ReportUtils.filters({
                ...filters,
                dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : "",
                dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : "",
            }, filterLabels),
        );
    }, [reportData]);

    const reportConfig = createConfig(filterLabels);

    useEffect(() => {
        if (!hasUrlFilters()) return;
        ReportUtils.fetchReport({currentFilters: filters, setLoading: setIsLoading, setReportData, setCollapsed, ...reportConfig});
    }, []);

    const apply = async () => {
        if (hasUrlFilters()) await ReportUtils.fetchReport({currentFilters: filters, setLoading: setIsLoading, setReportData, setCollapsed, ...reportConfig});

        ReportUtils.applyLoad({
            setLoading: setIsLoading,
            clearContent: () => {
                setReportData(null)
            }
        });

        const periodValidation = ReportUtils.periodValidate(filters.dtStart, filters.dtEnd, null, null);

        if (!periodValidation.ok) {
            ReportUtils.alertErrorMessage(periodValidation.message);
            setIsLoading(false);
            return;
        }

        try {
            const formattedFilters = {
                dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : null,
                dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : null,
                userId: filters.user?.map(i => i.value) ?? [],
            }

            const auditFilters = ReportUtils.filters({
                ...filters,
                dtStart: formattedFilters.dtStart,
                dtEnd: formattedFilters.dtEnd,
            }, filterLabels, true);

            const response = await EncounterXrayService.apply({ ...formattedFilters, filters: auditFilters });
            setReportData(response.data);

            const scale = window.visualViewport?.scale ?? 1;
            if (window.innerWidth <= 1440 || scale > 1) setCollapsed(true);

        } catch (error) {
            ReportUtils.alertErrorMessage("Erro ao gerar relatório.");
            console.error("Erro ao gerar o relatório", error);
        } finally {
            setIsLoading(false);
        }
    }

    const clearSearch = () => {
        ReportUtils.clearUrlFilters();
        setFilters(initialFilters);
        setReportData(null);
    }

    const toggleFilters = () => {
        if (!collapsed && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setFixedTop(rect.top);
        }
        setCollapsed(prev => !prev);
    }

    useGlobalKey("Enter", apply);

    const encounterCardHasData = (reportData?.encounterCards ?? []).some(item =>
        Number(item?.encounters ?? 0) > 0 ||
        Number(item?.individuals ?? 0) > 0 ||
        Number(item?.bo ?? 0) > 0 ||
        Number(item?.vehicles ?? 0) > 0 ||
        Number(item?.ind_association ?? 0) > 0 ||
        Number(item?.vehicle_association ?? 0) > 0
    );

    const weekDayTableHasData = (reportData?.encountersByWeekDayTable ?? []).some(item =>
        Number(item?.domingo ?? 0) > 0 ||
        Number(item?.segunda ?? 0) > 0 ||
        Number(item?.terca ?? 0) > 0 ||
        Number(item?.quarta ?? 0) > 0 ||
        Number(item?.quinta ?? 0) > 0 ||
        Number(item?.sexta ?? 0) > 0 ||
        Number(item?.sabado ?? 0) > 0
    );

    const hasData =
        !!reportData &&
        (
            encounterCardHasData ||
            (reportData.individualSex?.length ?? 0) > 0 ||
            (reportData.encountersByAgeRange?.length ?? 0) > 0 ||
            (reportData.individualsWithMoreEncounters?.length ?? 0) > 0 ||
            (reportData.encounterActions?.length ?? 0) > 0 ||
            (reportData.encountersByAgents?.length ?? 0) > 0 ||
            (reportData.encountersByTeams?.length ?? 0) > 0 ||
            (reportData.encountersByVehicleType?.length ?? 0) > 0 ||
            weekDayTableHasData ||
            (reportData.individuals_details?.length ?? 0) > 0 ||
            (reportData.vehicles_details?.length ?? 0) > 0
        );

    const noDataValue = hasData ? 1 : 0;

    return (
        <div className="flex">
            <Template isLoading={isLoading}>
                <Template.Header>
                    <Template.Title>{title}</Template.Title>
                    <HeaderButtons
                        exportable={true}
                        exportInSheets={true}
                        printable={true}
                        headerChange={true}
                        printComponentRef={printComponentRef}
                        reportData={reportData}
                        data={noDataValue}
                        headerSelected={headerSelected}
                        setHeaderSelected={setHeaderSelected}
                    />
                </Template.Header>
                <Template.Body className={`${collapsed ? "p-[0]" : ""}`}>
                    <Menu
                        buttonRef={buttonRef}
                        fixedTop={fixedTop}
                        collapsed={collapsed}
                        toggleFilters={toggleFilters}
                        clearSearch={clearSearch}
                        apply={apply}
                    >
                        <Filters filters={filters} setFilters={setFilters} />
                        <AutoDatetime setFilters={setFilters} />
                    </Menu>
                </Template.Body>
            </Template>
            <Card className={`w-full h-fit mr-2 ${!reportData || !hasData ? 'mt-[59px]' : 'mt-[61px]'} print:!mt-[-15px] transform ${!reportData || !hasData ? '!bg-[#f3f3f4] !border-none !-translate-y-[5px]' : ''} ${collapsed ? 'ml-2' : ''}`}>
                <Card.Body className={`flex-1 ${!reportData || !hasData ? 'p-[0]' : 'px-[20px] py-[5px]'}`}>
                    <NotAppliedContent reportData={reportData}/>
                    <NoDataContent reportData={reportData} data={noDataValue}/>
                    <Content
                        reportData={reportData}
                        data={noDataValue}
                        entity={headerSelected}
                        title={title}
                        collapsed={collapsed}
                        printComponentRef={printComponentRef}
                        setIsLoading={setIsLoading}
                        boName={boName}
                    />
                </Card.Body>
            </Card>
        </div>
    )
}